import { useNavigate, Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowRight, MapPin, ShieldCheck, Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '../components/academy/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecentInspections = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // index into photos for lightbox

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/recent-inspections`)
      .then((res) => {
        const flat = (res.data || []).flatMap((it) =>
          (it.photos || []).map((url) => ({ url, vehicle: it.vehicle, city: it.city, when: it.when }))
        );
        setPhotos(flat);
      })
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)), [photos.length]);
  const next = useCallback(() => setActive((i) => (i === null ? i : (i + 1) % photos.length)), [photos.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, close, prev, next]);

  const current = active !== null ? photos[active] : null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }} data-testid="recent-inspections">
      <Seo siteName="CarLens" title="Recent Inspection Photos | CarLens Pre-Purchase Inspections" description="Browse real photos from recent CarLens pre-purchase vehicle inspections across Northeast Florida. See the detail and quality that goes into every CarLens Vehicle Decision Report." path="/recent-inspections" />

      <section className="max-w-6xl mx-auto px-5 pt-20 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-orange-300 mb-5">
          <Camera className="w-4 h-4" /> Inspection Gallery
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]" style={{ fontFamily: 'Space Grotesk' }} data-testid="recent-inspections-h1">
          Recent Photos From Our Inspections
        </h1>
        <p className="mt-5 text-base md:text-xl text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          Real photos from vehicles our certified inspectors recently evaluated across Northeast Florida. Every CarLens report includes 30&ndash;50 detailed images like these. Customer details are always kept private.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-16">
        {loading ? (
          <div className="text-center py-16 text-zinc-500" data-testid="recent-inspections-loading">Loading inspection photos…</div>
        ) : photos.length === 0 ? (
          <div className="text-center py-16" data-testid="recent-inspections-empty">
            <p className="text-zinc-400 text-lg">Fresh inspection photos are on the way — check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="recent-inspections-gallery">
            {photos.map((p, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="group relative aspect-square overflow-hidden rounded-xl border border-zinc-800 hover:border-orange-500/50 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid={`inspection-photo-${i}`}
                aria-label={`View larger photo of ${p.vehicle}`}
              >
                <img
                  src={p.url}
                  alt={`Photo from a CarLens pre-purchase inspection of a ${p.vehicle}${p.city ? ` in ${p.city}` : ''}`}
                  className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-2 left-2 right-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-xs font-semibold text-white truncate">{p.vehicle}</p>
                  {p.city && <p className="inline-flex items-center gap-1 text-[11px] text-zinc-300"><MapPin className="w-3 h-3 text-orange-400" /> {p.city}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="inspection-lightbox"
        >
          <button onClick={close} className="absolute top-5 right-5 text-white/80 hover:text-white p-2" aria-label="Close" data-testid="lightbox-close">
            <X className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full bg-white/5 hover:bg-white/10"
            aria-label="Previous photo"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <div className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={current.url}
              alt={`CarLens pre-purchase inspection photo of a ${current.vehicle}${current.city ? ` in ${current.city}` : ''}`}
              className="max-w-full max-h-[80vh] w-auto h-auto rounded-lg shadow-2xl mx-auto"
            />
            <div className="mt-3 text-center">
              <p className="text-white font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{current.vehicle}</p>
              <p className="text-zinc-400 text-sm">
                {current.city ? `${current.city} · ` : ''}{active + 1} of {photos.length}
              </p>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full bg-white/5 hover:bg-white/10"
            aria-label="Next photo"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
      )}

      <section className="max-w-4xl mx-auto px-5 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-10 sm:p-14 text-center">
          <ShieldCheck className="w-10 h-10 text-white/90 mx-auto mb-3" />
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>Want this level of detail on your next car?</h2>
          <p className="mt-3 text-orange-50 text-lg max-w-xl mx-auto">Book a CarLens inspection and get your own Vehicle Decision Report™ — with 30–50 photos and a Confidence Score — before you buy.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/book')} className="rounded-full bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 h-auto text-base font-semibold" data-testid="recent-book-cta">Book an Inspection <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <Link to="/what-you-receive" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/60 text-white hover:bg-white/10 px-8 py-4 font-semibold transition-colors" data-testid="recent-sample-report-link">See a Sample Report</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecentInspections;
